(() => {
  'use strict';

  if (window.DeebHumidorBuyNow) return;

  const ROOT_SELECTOR = 'product-info[data-product-family="humidor"]';
  const BUTTON_SELECTOR = '[data-deeb-humidor-buy-now]';
  const PRIMARY_SUBMIT_SELECTOR = 'product-form [type="submit"][name="add"]';
  const QUANTITY_SELECTOR = '[data-deeb-humidor-quantity-guard] .quantity__input';
  const VARIANT_SELECTOR = 'form[data-type="add-to-cart-form"] [name="id"]';
  const ACTIVE_ATTRIBUTE = 'data-deeb-humidor-buy-now-active';
  const FALLBACK_TIMEOUT_MS = 12000;

  class HumidorBuyNowController {
    constructor(root) {
      this.root = root;
      this.button = root.querySelector(BUTTON_SELECTOR);
      this.formId = String(this.button?.dataset.productFormId || '').trim();
      this.form = this.formId ? document.getElementById(this.formId) : null;
      this.productForm = this.form?.closest('product-form') || null;
      this.primarySubmit = root.querySelector(PRIMARY_SUBMIT_SELECTOR);
      this.quantityInput = root.querySelector(QUANTITY_SELECTOR);
      this.variantInput =
        this.form?.querySelector('[name="id"]') || root.querySelector(VARIANT_SELECTOR);
      this.label = this.button?.querySelector('[data-deeb-humidor-buy-now-label]') || null;
      this.spinner = this.button?.querySelector('.loading__spinner') || null;
      this.defaultLabel = String(
        this.button?.dataset.defaultLabel || this.label?.textContent || 'Buy it now'
      ).trim();
      this.loadingLabel = String(
        this.button?.dataset.loadingLabel || 'Opening checkout…'
      ).trim();
      this.busy = false;
      this.pendingVariantId = '';
      this.timeoutId = null;
      this.destroyed = false;
      this.unsubscribers = [];

      if (!this.ready) return;

      this.button.addEventListener('click', this.handleClick);
      this.root.addEventListener('input', this.handleStateChange, true);
      this.root.addEventListener('change', this.handleStateChange, true);

      this.submitObserver = new MutationObserver(this.scheduleSync);
      this.submitObserver.observe(this.primarySubmit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });

      this.subscribeToCommerceLifecycle();
      this.syncState();
    }

    get ready() {
      return Boolean(
        this.button &&
          this.form &&
          this.productForm &&
          this.primarySubmit &&
          this.quantityInput &&
          this.variantInput &&
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
      );
    }

    get quantityIsValid() {
      return (
        Number.isFinite(this.quantityInput.valueAsNumber) &&
        this.quantityInput.checkValidity()
      );
    }

    get variantIsAvailable() {
      return Boolean(this.variantInput.value) && !this.variantInput.disabled;
    }

    get primarySubmitIsAvailable() {
      return (
        !this.primarySubmit.disabled &&
        this.primarySubmit.getAttribute('aria-disabled') !== 'true'
      );
    }

    subscribeToCommerceLifecycle() {
      this.unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate),
        subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError),
        subscribe(PUB_SUB_EVENTS.quantityUpdate, this.scheduleSync),
        subscribe(PUB_SUB_EVENTS.variantChange, this.scheduleSync)
      );
    }

    handleStateChange = (event) => {
      if (event.target !== this.quantityInput && event.target !== this.variantInput) return;
      this.scheduleSync();
    };

    scheduleSync = () => {
      queueMicrotask(() => this.syncState());
    };

    syncState() {
      if (!this.ready || this.destroyed) return;

      const disabled =
        this.busy ||
        !this.quantityIsValid ||
        !this.variantIsAvailable ||
        !this.primarySubmitIsAvailable;

      this.button.disabled = disabled;
      this.button.setAttribute('aria-disabled', String(disabled));
    }

    setBusy(busy) {
      this.busy = busy;
      this.button.classList.toggle('loading', busy);
      this.button.toggleAttribute('aria-busy', busy);
      this.spinner?.classList.toggle('hidden', !busy);

      if (this.label) {
        this.label.textContent = busy ? this.loadingLabel : this.defaultLabel;
      }

      if (busy) document.documentElement.setAttribute(ACTIVE_ATTRIBUTE, '');
      else document.documentElement.removeAttribute(ACTIVE_ATTRIBUTE);

      this.syncState();
    }

    clearFallback() {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    reset() {
      this.clearFallback();
      this.pendingVariantId = '';
      this.setBusy(false);
    }

    matchesPendingVariant(productVariantId) {
      return Boolean(
        this.busy &&
          this.pendingVariantId &&
          String(productVariantId || '') === this.pendingVariantId
      );
    }

    handleCartUpdate = ({ source, productVariantId } = {}) => {
      if (source !== 'product-form' || !this.matchesPendingVariant(productVariantId)) return;

      this.clearFallback();
      const checkoutUrl = `${window.Shopify?.routes?.root || '/'}checkout`;
      window.location.assign(checkoutUrl);
    };

    handleCartError = ({ source, productVariantId } = {}) => {
      if (source !== 'product-form' || !this.matchesPendingVariant(productVariantId)) return;
      queueMicrotask(() => this.reset());
    };

    handleClick = (event) => {
      event.preventDefault();

      if (this.busy) return;

      if (!this.quantityIsValid) {
        this.quantityInput.setAttribute('aria-invalid', 'true');
        this.quantityInput.reportValidity?.();
        this.quantityInput.focus();
        return;
      }

      if (!this.variantIsAvailable || !this.primarySubmitIsAvailable) {
        this.syncState();
        return;
      }

      this.quantityInput.removeAttribute('aria-invalid');
      this.pendingVariantId = String(this.variantInput.value || '');
      this.setBusy(true);

      try {
        this.form.requestSubmit(this.primarySubmit);
      } catch (error) {
        console.error(error);
        this.reset();
        return;
      }

      this.timeoutId = window.setTimeout(() => {
        if (this.root.isConnected) this.reset();
      }, FALLBACK_TIMEOUT_MS);
    };

    handlePageShow() {
      if (this.busy) this.reset();
      else this.syncState();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      this.button?.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('input', this.handleStateChange, true);
      this.root.removeEventListener('change', this.handleStateChange, true);
      this.submitObserver?.disconnect();
      this.unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      this.unsubscribers = [];
      this.clearFallback();
      document.documentElement.removeAttribute(ACTIVE_ATTRIBUTE);
    }
  }

  const instances = new Map();

  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) return null;
    if (instances.has(root)) return instances.get(root);

    const instance = new HumidorBuyNowController(root);
    if (!instance.ready) return null;

    instances.set(root, instance);
    return instance;
  };

  const initializeAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  const destroyRoot = (root) => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  };

  const destroyWithin = (scope) => {
    instances.forEach((instance, root) => {
      if (!root.isConnected || root === scope || scope?.contains?.(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  };

  document.addEventListener('product-info:loaded', (event) => {
    destroyRoot(event.target);
    initializeRoot(event.target);
  });
  document.addEventListener('shopify:section:load', (event) => {
    destroyWithin(event.target);
    initializeAll(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
  });
  window.addEventListener('pageshow', () => {
    instances.forEach((instance) => instance.handlePageShow());
    initializeAll();
  });

  initializeAll();

  window.DeebHumidorBuyNow = Object.freeze({
    get activeInstances() {
      return instances.size;
    },
  });
})();
